import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowLeft, Copy, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const { saveSession } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login"); // "login" | "register" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "register") {
        if (!name.trim()) { toast.error("Inserisci il tuo nome"); setLoading(false); return; }
        if (password.length < 6) { toast.error("La password deve essere di almeno 6 caratteri"); setLoading(false); return; }
        const r = await axios.post(`${API}/api/auth/register`, { name, email, password });
        saveSession(r.data);
        toast.success(`Benvenuto, ${r.data.user.name}!`);
        navigate('/app');
      } else {
        const r = await axios.post(`${API}/api/auth/login`, { email, password });
        saveSession(r.data);
        const role = r.data.user?.role;
        toast.success(`Bentornato, ${r.data.user.name}!`);
        if (role === "manager") navigate('/app/manager');
        else navigate('/app');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Inserisci la tua email"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/auth/forgot-password`, { email });
      if (r.data.sent) {
        // Email inviata con successo
        toast.success("Email inviata! Controlla la tua casella.");
        setResetToken("__email_sent__");
      } else if (r.data.reset_token) {
        // Fallback: mostra link direttamente (SMTP non disponibile)
        setResetToken(r.data.reset_token);
      } else {
        toast.info("Se l'email è registrata, riceverai le istruzioni.");
        setResetToken("__email_sent__");
      }
    } catch {
      toast.error("Errore nella richiesta");
    } finally {
      setLoading(false);
    }
  };

  const resetUrl = resetToken
    ? `${window.location.origin}/reset-password?token=${resetToken}`
    : null;

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-4">
      <div className="flex flex-col items-center gap-3 mb-8">
        <img src="/icon-192.png" alt="venditoreElite" className="w-16 h-16 rounded-2xl shadow-xl" />
        <div className="text-center">
          <h1 className="text-white font-bold text-2xl">venditoreElite</h1>
          <p className="text-white/40 text-sm">Calendario visite per agenti di commercio</p>
        </div>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* ── Forgot password view ────────────────────────── */}
        {tab === "forgot" ? (
          <div className="p-6 space-y-4">
            <button onClick={() => { setTab("login"); setResetToken(null); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Torna al login
            </button>
            <h2 className="font-bold text-[#0F172A]">Recupera password</h2>

            {!resetToken ? (
              <form onSubmit={handleForgot} className="space-y-3">
                <div>
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" data-testid="forgot-email-input" type="email"
                    placeholder="email@esempio.it" value={email}
                    onChange={e => setEmail(e.target.value)} className="mt-1" required />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0F172A] text-white"
                  data-testid="forgot-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link di reset"}
                </Button>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Riceverai un'email con il link per reimpostare la password.
                </p>
              </form>
            ) : resetToken === "__email_sent__" ? (
              <div className="space-y-3 text-center py-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="font-semibold text-[#0F172A]">Email inviata!</p>
                <p className="text-sm text-slate-500">Controlla la tua casella di posta (anche spam) e clicca il link.</p>
                <button onClick={() => { setTab("login"); setResetToken(null); }}
                  className="text-sm text-[#2563EB] hover:underline">Torna al login</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">Email non disponibile. Copia il link qui sotto.</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-3 text-xs text-slate-600 break-all">
                  {`${window.location.origin}/reset-password?token=${resetToken}`}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${resetToken}`); toast.success("Link copiato!"); }}
                  className="flex items-center gap-2 w-full justify-center bg-[#0F172A] text-white py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800 transition-colors"
                  data-testid="copy-reset-link-btn">
                  <Copy className="w-4 h-4" /> Copia link
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ── Login / Register tabs ─────────────────────── */}
            <div className="flex border-b border-slate-100">
              {["login", "register"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`flex-1 py-3.5 text-sm font-semibold transition-colors ${tab === t ? "text-[#0F172A] border-b-2 border-[#0F172A]" : "text-slate-400 hover:text-slate-600"}`}
                  data-testid={`tab-${t}`}>
                  {t === "login" ? "Accedi" : "Registrati"}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              <button onClick={handleGoogleLogin}
                className="flex items-center justify-center gap-3 w-full border border-slate-200 rounded-xl py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="google-login-btn">
                <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/><path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.347 2.825.957 4.039l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/></svg>
                Continua con Google
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-xs text-slate-400">oppure</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                {tab === "register" && (
                  <div>
                    <Label htmlFor="auth-name">Nome</Label>
                    <Input id="auth-name" data-testid="auth-name" placeholder="Il tuo nome"
                      value={name} onChange={e => setName(e.target.value)} className="mt-1" required />
                  </div>
                )}
                <div>
                  <Label htmlFor="auth-email">Email</Label>
                  <Input id="auth-email" data-testid="auth-email" type="email"
                    placeholder="email@esempio.it" value={email}
                    onChange={e => setEmail(e.target.value)} className="mt-1" required />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="auth-password">Password</Label>
                    {tab === "login" && (
                      <button type="button" onClick={() => setTab("forgot")}
                        className="text-xs text-[#2563EB] hover:underline" data-testid="forgot-password-link">
                        Password dimenticata?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input id="auth-password" data-testid="auth-password"
                      type={showPwd ? "text" : "password"} placeholder="••••••••"
                      value={password} onChange={e => setPassword(e.target.value)} className="pr-10" required />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0F172A] text-white mt-2"
                  data-testid="auth-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === "login" ? "Accedi" : "Crea account"}
                </Button>
              </form>
            </div>
          </>
        )}
      </div>

      <p className="text-white/30 text-xs mt-6 text-center">
        Usando l'app accetti la <a href="/privacy-policy" className="underline hover:text-white/60">Privacy Policy</a>
      </p>
    </div>
  );
}
